import _ from "lodash"
import React from "react"

import { useAppSelector } from "../app/hooks"
import { CommonText, FieldName, FilterKey, FlightText } from "../common/text"
import { IFieldValues } from "../containers/selectionSlice"
import { IFieldConfig } from "../pages/Config"
import * as Utils from "./Utils"

interface FilterValueLabel {
  label: string
  value: string
  filterKey: FilterKey
}

interface Props {
  historyValues: IFieldValues
  showSeasonTime?: boolean
  excludeFields: IFieldConfig
  onClick?: () => void
  chosen?: boolean
}

const {
  airports,
  acReg,
  acType,
  code,
  ad,
  origin,
  dest,
  last,
  date,
  serviceNo,
  clearTimeRange,
  requiredTimeRange,
  operators,
  operator,
  st,
  excluded,
  waitlist,
  noslotsOnly,
  excludeNoslots,
  slotId,
  noSlotFlightIOnly,
  excludeSlotsFlights,
  delink,
  normalizePeriod
} = FlightText

const filterOrder = [
  FilterKey.season,
  FilterKey.airports,
  FilterKey.airport,
  FilterKey.operators,
  FilterKey.serviceNumber,
  FilterKey.serviceNumberRange,
  FilterKey.serviceTypes,
  FilterKey.flightMode,
  FilterKey.constraintType,
  FilterKey.terminal,
  FilterKey.valueType,
  FilterKey.schedule,
  FilterKey.singleDate,
  FilterKey.startDate,
  FilterKey.endDate,
  FilterKey.dateRange,
  FilterKey.doop,
  FilterKey.clearedTimeRange,
  FilterKey.requiredTimeRange,
  FilterKey.clearCode,
  FilterKey.aircraftType,
  FilterKey.aircraftRegistration,
  FilterKey.slotId,
  FilterKey.origDest,
  FilterKey.prevNext,
  FilterKey.lastUpdate,
  FilterKey.canxSinceDate,
  FilterKey.adt,
  FilterKey.dayOfWeek,
  FilterKey.date,
  FilterKey.hour,
  FilterKey.normalizePeriod,
  FilterKey.delink,
  FilterKey.waitlist,
  FilterKey.excludeNoslots,
  FilterKey.noslotsOnly
]

const fieldNameFilterKeyMap = {
  [FilterKey.serviceNumber]: FieldName.serviceNumber,
  [FilterKey.serviceNumberRange]: FieldName.serviceNumberTo,
  [FilterKey.dateRange]: FieldName.startDate,
  [FilterKey.singleDate]: FieldName.startDate,
  [FilterKey.clearedTimeRange]: FieldName.clearedTimeTo,
  [FilterKey.requiredTimeRange]: FieldName.requiredTimeTo
}

const { next } = CommonText
const HistoryValueRow: React.FunctionComponent<Props> = ({
  historyValues,
  showSeasonTime,
  excludeFields,
  onClick,
  chosen
}) => {
  const { season, useLocalTime } =
    useAppSelector(
      (state) => state["selectionReducer"].airportSeasonsSelector
    ) || {}

  const adtValueMap = {
    T: "TOT",
    A: "ARR",
    D: "DEP",
    L: "ALL"
  }

  const dayOfWeekValueMap = {
    1: "Mon",
    2: "Tue",
    3: "Wed",
    4: "Thu",
    5: "Fri",
    6: "Sat",
    7: "Sun"
  }

  const valueTypeValueMap = {
    heldSlots: "Held Slots",
    loadCapacity: "Load/Capacity",
    availability: "Availability"
  }

  const handleGetFilters = (filters) => {
    let filterElements: FilterValueLabel[] = []
    const clearedTimes = {}
    const requiredTimes = {}
    const dates = {}
    const operator = {}
    const serviceNumber = {}

    if (showSeasonTime) {
      const seasonTimeFilterElements: FilterValueLabel[] = [
        {
          label: "Season",
          value: season,
          filterKey: FilterKey.season
        },
        {
          label: useLocalTime ? "Local" : "UTC",
          filterKey: FilterKey.time,
          value: null
        }
      ]
      filterElements = filterElements.concat(seasonTimeFilterElements)
    }

    Object.keys(filters).forEach((k) => {
      interface IData {
        label: string
        value: string
      }
      interface IConfig {
        [key: string]: string
      }
      if (filters[k] !== null && filters[k] !== "") {
        let f = null
        const rangeObj = (
          objLength: number,
          fieldName: string,
          valObj: IConfig,
          filterKeys: string[],
          filter: string
        ) => {
          let finishObj: IData
          if (objLength === 2) {
            finishObj = handleGetFilter(filterKeys[0], valObj)
          }

          if (objLength === 1 && k !== fieldName) {
            finishObj = handleGetFilter(filterKeys[1], valObj)
          }

          if (!filter) {
            finishObj = handleGetFilter(filterKeys[2], valObj)
          }

          return finishObj
        }

        if (k === FieldName.clearedTimeFrom || k === FieldName.clearedTimeTo) {
          clearedTimes[k] = filters[k]
          const objLength = Object.keys(clearedTimes).length

          f = rangeObj(
            objLength,
            FieldName.clearedTimeFrom,
            clearedTimes,
            [
              FilterKey.clearedTimeRange,
              FilterKey.clearedTimeRangeFrom,
              FilterKey.clearedTimeRangeTo
            ],
            filters.clearedTimeTo
          )
        } else if (
          k === FieldName.requiredTimeFrom ||
          k === FieldName.requiredTimeTo
        ) {
          requiredTimes[k] = filters[k]
          const objLength = Object.keys(requiredTimes).length

          f = rangeObj(
            objLength,
            FieldName.requiredTimeFrom,
            requiredTimes,
            [
              FilterKey.requiredTimeRange,
              FilterKey.requiredTimeRangeFrom,
              FilterKey.requiredTimeRangeTo
            ],
            filters.requiredTimeTo
          )
        } else if (
          k === FieldName.serviceNumber ||
          k === FieldName.serviceNumberTo
        ) {
          serviceNumber[k] = filters[k]
          f =
            Object.keys(serviceNumber).length === 2
              ? handleGetFilter(FilterKey.serviceNumberRange, serviceNumber)
              : Object.keys(serviceNumber).length === 1 &&
                !filters[FieldName.serviceNumberTo]
              ? handleGetFilter(FilterKey.serviceNumber, serviceNumber)
              : null
        } else if (k === FieldName.startDate || k === FieldName.endDate) {
          dates[k] = filters[k]
          const objLength = Object.keys(dates).length

          const datesObj = () => {
            let dateObj: IData
            if (objLength === 2) {
              dateObj = handleGetFilter(FilterKey.dateRange, dates)
            }

            if (objLength === 2 && filters.startDate === filters.endDate) {
              dateObj = handleGetFilter(FilterKey.singleDate, dates)
            }

            if (objLength === 1 && k !== FieldName.startDate) {
              dateObj = handleGetFilter(FilterKey.endDate, dates)
            }

            if (!filters.endDate) {
              dateObj = handleGetFilter(
                excludeFields.endDate
                  ? FilterKey.singleDate
                  : FilterKey.startDate,
                dates
              )
            }

            return dateObj
          }

          f = datesObj()
        } else if (k === FieldName.operators) {
          operator[k] = filters[k]
          operator[FieldName.excludeOper] = filters[FieldName.excludeOper]
          f = handleGetFilter(FilterKey.operators, operator)
        } else if (k === FieldName.waitlist) {
          if (filters[k]) {
            f = handleGetFilter(FilterKey.waitlist, waitlist)
          } else {
            f = null
          }
        } else if (k === FieldName.noslotsOnly) {
          if (filters[k]) {
            f = handleGetFilter(FilterKey.noslotsOnly, noslotsOnly)
          } else {
            f = null
          }
        } else if (k === FieldName.normalizePeriod) {
          if (filters[k]) {
            f = handleGetFilter(FilterKey.normalizePeriod, normalizePeriod)
          } else {
            f = null
          }
        } else if (k === FieldName.delink) {
          if (filters[k]) {
            f = handleGetFilter(FilterKey.delink, delink)
          } else {
            f = null
          }
        } else if (k === FieldName.excludeNoslots) {
          if (filters[k]) {
            f = handleGetFilter(FilterKey.excludeNoslots, excludeNoslots)
          } else {
            f = null
          }
        } else if (k === FieldName.slotId) {
          f = handleGetFilter(FilterKey.slotId, filters[k])
        } else {
          f = handleGetFilter(k, filters[k])
        }

        if (f) {
          filterElements.push(f)
        }
      }
    })

    const sortedFilterElements = _.cloneDeep(filterElements).sort((a, b) => {
      return filterOrder.indexOf(a.filterKey) - filterOrder.indexOf(b.filterKey)
    })

    return sortedFilterElements.map((e, idx) => {
      const filterKey = fieldNameFilterKeyMap[e.filterKey] ?? e.filterKey
      const excludeFilter = excludeFields[filterKey]
      return (
        <div
          key={idx}
          className={
            "ocs-search-history-row-filter" +
            (excludeFilter ? " excluded-filter" : "")
          }
        >
          <p className={e.filterKey === FilterKey.time ? " time-filter" : ""}>
            {e.label}
            {e.value && ":"}
          </p>
          <p>{e.value}</p>
          {idx + 1 !== filterElements.length && (
            <p className="ocs-search-history-row-filter-bullet">∙</p>
          )}
        </div>
      )
    })
  }

  const handleGetFilter = (
    filterKey: string,
    filterValue: any
  ): FilterValueLabel => {
    switch (filterKey) {
      case FilterKey.airports:
        return {
          label: airports,
          value: filterValue.join(", "),
          filterKey: FilterKey.airports
        }
      case FilterKey.airport:
        return {
          label: "Airport",
          value: filterValue[0],
          filterKey: FilterKey.airport
        }
      case FilterKey.serviceTypes:
        return filterValue.length
          ? {
              label: st,
              value: filterValue.join(", "),
              filterKey: FilterKey.serviceTypes
            }
          : null
      case FilterKey.terminal:
        return {
          label: filterKey,
          value: filterValue,
          filterKey: FilterKey.terminal
        }
      case FilterKey.operators:
        return filterValue.operators.length
          ? {
              label:
                (filterValue.operators.length > 1 ? operators : operator) +
                (filterValue.excludeOper ? ` ${excluded}` : ""),
              value: filterValue.operators.join(", "),
              filterKey: FilterKey.operators
            }
          : null
      case FilterKey.aircraftRegistration:
        return {
          label: acReg,
          value: filterValue,
          filterKey: FilterKey.aircraftRegistration
        }
      case FilterKey.slotId:
        return {
          label: slotId,
          value: filterValue,
          filterKey: FilterKey.slotId
        }
      case FilterKey.aircraftType:
        return {
          label: acType,
          value: filterValue,
          filterKey: FilterKey.aircraftType
        }
      case FilterKey.clearCode:
        return {
          label: code,
          value: filterValue,
          filterKey: FilterKey.clearCode
        }
      case FilterKey.flightMode:
        return {
          label: ad,
          value: filterValue,
          filterKey: FilterKey.flightMode
        }
      case FilterKey.origDest:
        return {
          label: `${origin}/${dest}`,
          value: filterValue,
          filterKey: FilterKey.origDest
        }
      case FilterKey.prevNext:
        return {
          label: `${last}/${next}`,
          value: filterValue,
          filterKey: FilterKey.prevNext
        }
      case FilterKey.serviceNumber:
        return {
          label: serviceNo,
          value: filterValue.serviceNumber || filterValue.serviceNumberTo,
          filterKey: FilterKey.serviceNumber
        }
      case FilterKey.serviceNumberRange:
        return {
          label: serviceNo,
          value:
            filterValue.serviceNumber + " - " + filterValue.serviceNumberTo,
          filterKey: FilterKey.serviceNumberRange
        }
      case FilterKey.doop:
        return {
          label: filterKey.toUpperCase(),
          value: filterValue,
          filterKey: FilterKey.doop
        }
      case FilterKey.clearedTimeRange:
        return {
          label: clearTimeRange,
          value:
            filterValue.clearedTimeFrom + " - " + filterValue.clearedTimeTo,
          filterKey: FilterKey.clearedTimeRange
        }
      case FilterKey.clearedTimeRangeFrom:
        return {
          label: clearTimeRange,
          value: "* - " + filterValue.clearedTimeTo,
          filterKey: FilterKey.clearedTimeRange
        }
      case FilterKey.clearedTimeRangeTo:
        return {
          label: clearTimeRange,
          value: filterValue.clearedTimeFrom + " - *",
          filterKey: FilterKey.clearedTimeRange
        }
      case FilterKey.requiredTimeRange:
        return {
          label: requiredTimeRange,
          value:
            filterValue.requiredTimeFrom + " - " + filterValue.requiredTimeTo,
          filterKey: FilterKey.requiredTimeRange
        }

      case FilterKey.requiredTimeRangeFrom:
        return {
          label: requiredTimeRange,
          value: "* - " + filterValue.requiredTimeTo,
          filterKey: FilterKey.requiredTimeRange
        }
      case FilterKey.normalizePeriod:
        return {
          label: normalizePeriod,
          value: filterValue.normalizePeriod,
          filterKey: FilterKey.normalizePeriod
        }
      case FilterKey.delink:
        return {
          label: delink,
          value: filterValue.delink,
          filterKey: FilterKey.delink
        }

      case FilterKey.waitlist:
        return {
          label: waitlist,
          value: filterValue.waitlist,
          filterKey: FilterKey.waitlist
        }
      case FilterKey.noslotsOnly:
        return {
          label: noSlotFlightIOnly,
          value: null,
          filterKey: FilterKey.noslotsOnly
        }
      case FilterKey.excludeNoslots:
        return {
          label: excludeSlotsFlights,
          value: null,
          filterKey: FilterKey.excludeNoslots
        }
      case FilterKey.requiredTimeRangeTo:
        return {
          label: requiredTimeRange,
          value: filterValue.requiredTimeFrom + " - *",
          filterKey: FilterKey.requiredTimeRange
        }
      case FilterKey.dateRange:
        return {
          label: FlightText.dateRange,
          value:
            Utils.formatDateShort(filterValue.startDate) +
            " - " +
            Utils.formatDateShort(filterValue.endDate),
          filterKey: FilterKey.dateRange
        }

      case FilterKey.singleDate:
        return {
          label: date,
          value: Utils.formatDateShort(filterValue.startDate),
          filterKey: FilterKey.singleDate
        }
      case FilterKey.startDate:
        return {
          label: FlightText.dateRange,
          value: Utils.formatDateShort(filterValue.startDate) + " - *",
          filterKey: FilterKey.startDate
        }
      case FilterKey.endDate:
        return {
          label: FlightText.dateRange,
          value: "* - " + Utils.formatDateShort(filterValue.endDate),
          filterKey: FilterKey.endDate
        }
      case FilterKey.lastUpdate:
        return {
          label: "Last Update",
          value: Utils.formatDateShortwYear(filterValue),
          filterKey: FilterKey.lastUpdate
        }
      case FilterKey.canxSinceDate:
        return {
          label: "Cancelled Since Date",
          value: Utils.formatDateShortwYear(filterValue),
          filterKey: FilterKey.canxSinceDate
        }
      case FilterKey.constraintType:
        return {
          label: FlightText.constraint,
          value: filterValue,
          filterKey: FilterKey.constraintType
        }
      case FilterKey.valueType:
        return {
          label: FlightText.value,
          value: valueTypeValueMap[filterValue],
          filterKey: FilterKey.constraintType
        }
      case FilterKey.hour:
        return {
          label: FlightText.hour,
          value: filterValue,
          filterKey: FilterKey.hour
        }
      case FilterKey.date:
        return {
          label: date,
          value: Utils.formatDateShort(filterValue),
          filterKey: FilterKey.date
        }
      case FilterKey.schedule:
        return {
          label: FlightText.schedule,
          value: filterValue,
          filterKey: FilterKey.schedule
        }
      case FilterKey.adt:
        return {
          label: FlightText.adt,
          value: adtValueMap[filterValue],
          filterKey: FilterKey.adt
        }
      case FilterKey.dayOfWeek:
        return {
          label: FlightText.doop,
          value: dayOfWeekValueMap[filterValue],
          filterKey: FilterKey.dayOfWeek
        }
      default:
        return null
    }
  }

  return (
    <div
      className={
        "ocs-search-history-row d-flex align-items-top" +
        (typeof onClick === "function"
          ? " ocs-search-history-row-clickable"
          : "") +
        (chosen ? " ocs-search-history-row-chosen" : "")
      }
      onClick={() => onClick && onClick()}
    >
      <div className="ocs-search-history-row-icon" />
      <div className="d-flex flex-wrap mq-flex-direction-row">
        {handleGetFilters(historyValues)}
      </div>
    </div>
  )
}

export default HistoryValueRow
